//src/features/dashboard/Dashboard.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { signOut } from 'firebase/auth';
import { functions, auth, db, tCollection, tDoc, tSubCollection } from '../../firebase';
import { TENANT_ID } from "../../tenant";

import { deleteDoc, doc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { Sparkles, Globe } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDashboardData } from './useDashboardData';
import DashboardMessageBlock from './components/DashboardMessageBlock';
import PossessionsCard from './components/PossessionsCard';
import AccountStatusCard from './components/AccountStatusCard';
import AccountLinkCard from '../auth/AccountLinkCard';
import ReservationsCard from './components/ReservationsCard';
import SystemMessage from './components/SystemMessage';

import PoweredByAkuto from '../../components/PoweredByAkuto';
import MemberQRCode from './components/MemberQRCode';
import Loader from '../../components/Loader';
import ActiveTimeBlock from './components/ActiveTimeBlock';
import { httpsCallable } from "firebase/functions";
import { InstallGuideSheet, useInstallState } from "../../components/InstallGuide";

import {
  consumeResumeShopAfterAuth,
  clearPublicShopCart,
  loadCheckoutIntent,
  clearCheckoutIntent,
} from "../../lib/publicShopSession";

// ✅ 追加：プロフィール編集フォーム
import FacilityStatusCard from "./components/FacilityStatusCard";
import DeviceAccountGate from "../auth/DeviceAccountGate";

// ⚠開いたときにしか使わない画面は最初のバンドルから外す(LINE から開いたときの読み込みを軽くする)。
//   LINE の webview はセッションを跨いで HTTP キャッシュが効かないことがあり、毎回バンドルを
//   取り直す(Core の /card で実測 7〜8 秒)。ホームが出たあとに裏で先読みしておくので、
//   ボタンを押したときに待つことはほぼ無い。
const loadShopModal = () => import('./components/ShopModalLocal');
const loadBookingModal = () => import('./components/BookingModal');
const loadPaymentHistoryModal = () => import('./PaymentHistoryModal');
const loadProfileEditForm = () => import('../user/ProfileEditForm');
const ShopModalLocal = lazy(loadShopModal);
const BookingModal = lazy(loadBookingModal);
const PaymentHistoryModal = lazy(loadPaymentHistoryModal);
const ProfileEditForm = lazy(loadProfileEditForm);

const prefetchDashboardModals = () => {
  const run = () => {
    [loadShopModal, loadBookingModal, loadPaymentHistoryModal, loadProfileEditForm]
      .forEach((load) => load().catch(() => {}));
  };
  if (typeof window !== "undefined" && window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 1500);
  }
};
//import { ensureCustomer } from "../../lib/ensureCustomer";


const DashboardInner = ({
  user,
  startFreshProfileRequest,
  onStartFreshProfileConsumed,
}) => {
  // ✅ Hook は必ずコンポーネント内
  const location = useLocation();
  const navigate = useNavigate();
  const checkoutIntent = loadCheckoutIntent();
  const isResumingShopFlow = Boolean(checkoutIntent?.type);

  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [profilePromptReason, setProfilePromptReason] = useState("");
    // ✅ 追加：プロフィール編集モーダルの開閉
  const [checkingGender, setCheckingGender] = useState(true);

    useEffect(() => {
    if (!startFreshProfileRequest) return;

    setShowGenderModal(true);
    setShowProfileEdit(false);
    setProfilePromptReason(
      startFreshProfileRequest.reason || "start_fresh_profile"
    );

    onStartFreshProfileConsumed?.();
  }, [startFreshProfileRequest, onStartFreshProfileConsumed]);

  // ✅ location.state の token を「一度だけ」取り込むローカル state
  const [highlightGiftTokenOnce, setHighlightGiftTokenOnce] = useState(null);
  const [highlightReservationIdOnce, setHighlightReservationIdOnce] = useState(null);
  const [highlightInviteTokenOnce, setHighlightInviteTokenOnce] = useState(null);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  // ホーム画面に追加の案内(全お客様アプリ共通)【AKUTOブランド基準 10-11】
  const install = useInstallState();
  const [bookingResumeDraft, setBookingResumeDraft] = useState(null);

  // ✅ 自動オープンは1回だけ
  const didAutoOpenPossessionsRef = useRef(false);
  const didAutoOpenReservationsRef = useRef(false);

  useEffect(() => {
    const giftToken = location?.state?.highlightGiftToken || null;
    const reservationId = location?.state?.highlightReservationId || null;
    const inviteToken = location?.state?.highlightInviteToken || null;
    const autoOpenAccordion = location?.state?.autoOpenAccordion || null;

    if (!giftToken && !reservationId && !inviteToken && !autoOpenAccordion) return;

    if (giftToken) {
      setHighlightGiftTokenOnce((prev) => prev ?? String(giftToken));
    }

    if (reservationId) {
      setHighlightReservationIdOnce((prev) => prev ?? String(reservationId));
    }

    if (inviteToken) {
      setHighlightInviteTokenOnce((prev) => prev ?? String(inviteToken));
    }

    if (autoOpenAccordion === "possessions") {
      setActiveAccordion("possessions");
    }

    if (autoOpenAccordion === "reservations") {
      setActiveAccordion("reservations");
    }

    navigate(location.pathname + location.search, { replace: true, state: null });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state]);

  useEffect(() => {
    if (!highlightGiftTokenOnce) return;
    if (didAutoOpenPossessionsRef.current) return;
    didAutoOpenPossessionsRef.current = true;

    // ✅ 急いで確認したいので所持品を自動で開く
    setActiveAccordion("possessions");
  }, [highlightGiftTokenOnce]);

  useEffect(() => {
    if (!highlightReservationIdOnce && !highlightInviteTokenOnce) return;
    if (didAutoOpenReservationsRef.current) return;

    didAutoOpenReservationsRef.current = true;
    setActiveAccordion("reservations");
  }, [highlightReservationIdOnce, highlightInviteTokenOnce]);

  useEffect(() => {
    if (!highlightGiftTokenOnce) return;
    const timer = setTimeout(() => setHighlightGiftTokenOnce(null), 12000); // 12秒で解除
    return () => clearTimeout(timer);
  }, [highlightGiftTokenOnce]);

  useEffect(() => {
    if (!highlightReservationIdOnce && !highlightInviteTokenOnce) return;

    const timer = setTimeout(() => {
      setHighlightReservationIdOnce(null);
      setHighlightInviteTokenOnce(null);
    }, 12000);

    return () => clearTimeout(timer);
  }, [highlightReservationIdOnce, highlightInviteTokenOnce]);


  // ホームが出たら、開く画面の部品を裏で先読みしておく
  useEffect(() => {
    prefetchDashboardModals();
  }, []);

  // ✅ PossessionsCard へ渡す token（これだけ使う）
  const highlightGiftToken = highlightGiftTokenOnce;

  // ===== debug: console からIDトークン取得 =====
  useEffect(() => {
    if (typeof window === "undefined") return;

    window.dumpIdToken = async () => {
      const u = auth.currentUser;
      if (!u) { console.log("no user"); return null; }
      const t = await u.getIdToken(true);
      console.log("ID_TOKEN:", t);
      return t;
    };
  }, []);

  const {
    memberData,
    rawPaymentHistory,
    activeSubscriptions,
    inventory,
    activeItems,
    clearActiveItem,
    activeReservationItem,
    clearActiveReservationItem,
    systemMessage,
    productMap,
    reservations,
    loading,
    entitlements,
    customerUid, // ← これを戻す
    pendingGifts,
    pendingReservationCount,
    planCard,
    planActiveEnt,   // ← 追加

  } = useDashboardData(user);

    const profileReady = !loading && memberData !== undefined;

    const hasRequiredProfile = useMemo(() => {
      const p = memberData || {};
      const md = p?.metadata || {};

      const nameOk = !!String(p?.name || "").trim();
      const genderOk = !!String(md?.gender || p?.gender || "").trim();
      const birthdayOk = !!String(md?.birthday || "").trim();

      return nameOk && genderOk && birthdayOk;
    }, [memberData]);

    useEffect(() => {
      if (!memberData) return;

      if (isResumingShopFlow) {
        setShowGenderModal(false);
        setCheckingGender(false);
        return;
      }

      const shouldOpen = !hasRequiredProfile;
      setShowGenderModal(shouldOpen);

      if (shouldOpen && !profilePromptReason) {
        setProfilePromptReason("incomplete_profile");
      }

      if (!shouldOpen) {
        setProfilePromptReason("");
      }

      setCheckingGender(false);
    }, [memberData, hasRequiredProfile, isResumingShopFlow, profilePromptReason]);

  const [activeAccordion, setActiveAccordion] = useState(null);
  const [showShop, setShowShop] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showBooking, setShowBooking] = useState(false); 
  // ✅ Booking: プリロード + ふわっと表示用（mounted と open を分ける）
  const [bookingMounted, setBookingMounted] = useState(false);
  
  const openProfileEdit = () => setShowProfileEdit(true);
  const closeProfileEdit = () => setShowProfileEdit(false);

  //const [progress, setProgress] = useState(100);

  const possessionsRef = useRef(null);

  const reservationsRef = useRef(null);
  const statusRef = useRef(null);

  // ✅ Shop 商品カタログを事前にプリロード（モーダルを開いた瞬間にローディングを出さない）
  const [shopCatalog, setShopCatalog] = useState({ ticket: [], goods: [], plan: [] });
  const [shopCatalogReady, setShopCatalogReady] = useState(false);

  // ✅ app_config を Firestore から読む（ヘッダー色 / ロゴ）
  const [appConfig, setAppConfig] = useState({
    app_title: 'suomi',
    header_color: '#0B1220',
    logo_url: '',
    logo_path: '',
    terms_url: '',
  });

  // ホーム画面に置いたときの名前=店の名前(iPhoneは追加する瞬間のこの値を使う)
  useEffect(() => {
    const title = String(appConfig?.app_title || '').trim();
    if (!title) return;
    const meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (meta) meta.setAttribute('content', title);
  }, [appConfig?.app_title]);

  // ✅ Booking（予約）用プリロード（level1）
const [bookingPrefetch, setBookingPrefetch] = useState({
  services: [],
  productMap: {},
  myTickets: [],
  activeSubscriptions: [],
});
const [bookingPrefetchReady, setBookingPrefetchReady] = useState(false);

// Booking用：customerKey（customers/{docId}）
const getBookingCustomerKey = useCallback(() => {
  const c = String(customerUid || '').trim();
  if (c) return c;
  const u = String(user?.uid || '').trim();
  if (u) return u;
  return '';
}, [customerUid, user?.uid]);

const clampInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : def;
};

// entitlements -> ticket list 変換（Booking側の期待形式）
const buildTicketsFromEntitlementsSnap = (eSnap) => {
  const list = eSnap.docs
    .map((docSnap) => {
      const data = docSnap.data() || {};
      const type = String(data.product_type || data.type || '').toLowerCase();
      if (type !== 'ticket') return null;

      const total = clampInt(data.quantity_total ?? 0, 0);
      const used = clampInt(data.quantity_used ?? 0, 0);
      const remaining = total - used;

      const isActive = data.is_active !== false && data.active !== false;
      if (!(remaining > 0) || !isActive) return null;

      const name =
        data.product_name ||
        data.name ||
        data.title ||
        data.description ||
        'チケット';

      return {
        id: docSnap.id,
        name,
        remaining,
        productId: data.product_id || data.productId || null,
        originalData: data,
      };
    })
    .filter(Boolean);

  return list;
};

  const hasActivePlan = useMemo(() => {
    return (Array.isArray(entitlements) ? entitlements : []).some(
      (e) => String(e?.product_type || '').toLowerCase() === 'plan' && e?.is_active === true
    );
  }, [entitlements]);

  const isValidHttpUrl = (v) => {
    try {
      const u = new URL(String(v || '').trim());
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const ref = tDoc('config', 'app', TENANT_ID); // tenants/{TENANT_ID}/config/app
    const unsub = onSnapshot(
      ref,
      (snap) => {
        console.log('[Dashboard] app_config path=', ref.path, 'exists=', snap.exists());
        if (snap.exists()) {
          const data = snap.data() || {};
          setAppConfig((prev) => ({ ...prev, ...data }));
        }
      },
      (e) => {
        console.error('[Dashboard] app_config onSnapshot error:', e, 'path=', ref.path);
      }
    );
    return () => unsub();
  }, []);

// 1) スクロール用 effect は「開いた時だけ」にする（閉じた時は何もしない）
useEffect(() => {
  if (!activeAccordion) return;

  const refs = { possessions: possessionsRef, reservations: reservationsRef, status: statusRef };
  const targetRef = refs[activeAccordion];
  if (!targetRef?.current) return;

  requestAnimationFrame(() => {
    targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}, [activeAccordion]);

// 2) “閉じる操作” のときだけ top に戻す
const handleToggle = (id) => {
  setActiveAccordion((prev) => {
    const next = (prev === id ? null : id);

    if (prev === id) {
      // ✅ 閉じる操作に紐づけてトップへ
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
    return next;
  });
};

  const anyModalOpen = showShop || showBooking || showHistory || showProfileEdit;

  useEffect(() => {
    if (!anyModalOpen) {
      document.body.style.position = "";
      document.body.style.overflow = "";
      document.body.style.top = "";
      return;
    }

    const scrollY = window.scrollY;
    const body = document.body;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [anyModalOpen]);

  useEffect(() => {
    const handleCancelReturn = async () => {
      if (!user) return;

      const params = new URLSearchParams(window.location.search);
      const action = String(params.get("action") || "");
      const reservationId = String(params.get("rid") || params.get("reservationId") || "");

      if (action !== "cancel_payment" || !reservationId) return;

      try {
        const fn = httpsCallable(functions, "cancelReservationPending");
        await fn({
          tenantId: TENANT_ID,
          reservationId,
          reason: "cancel_url_return",
        });
      } catch (e) {
        console.error("[cancelReservationPending] error:", e);
      } finally {
        // ✅ クエリだけ消して二重実行防止（他クエリがあれば残す）
        params.delete("action");
        params.delete("rid");
        params.delete("reservationId");

        const next =
          window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState(null, "", next);
      }
    };

    handleCancelReturn();
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = String(params.get("session_id") || "").trim();

    if (!sessionId) return;

    clearPublicShopCart();

    setProfilePromptReason("after_purchase");

    params.delete("session_id");
    params.delete("rid");
    params.delete("reservationId");
    params.delete("mode");

    const next =
      window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState(null, "", next);
  }, []);

  // =========================
  // ✅ Shop catalog (A + C)
  // A: すぐ開く（キャッシュ即時反映）
  // C: onSnapshot で server 更新が自動反映（最新価格が遅れて入る）
  // 前提：products ドキュメントに unit_amount / active_price_id が入っている（正）
  // =========================

  const buildCatalogFromProductsSnap = (productsSnap) => {
    const all = { ticket: [], goods: [], plan: [] };

    productsSnap.forEach((d) => {
      const data = d.data() || {};
      const type = data?.metadata?.product_type || "ticket";
      const normalizedType = ["ticket", "goods", "plan"].includes(type) ? type : "ticket";

      // ★正：products の unit_amount / active_price_id
      const unitAmount = Number(data?.unit_amount ?? 0) || 0;
      const currency = String(data?.currency || "jpy");

      const priceId =
        String(data?.active_price_id || "").trim() ||
        String(data?.default_price || "").trim() ||
        ""; // 最低限

      // ShopModalLocal 側は p.unit_amount / active_price_id を見るが、互換のため price も付ける
      const activePrice = {
        id: priceId,
        unit_amount: unitAmount,
        currency,
        active: true,
      };

      all[normalizedType].push({
        id: d.id,
        ...data,
        type: normalizedType,
        price: activePrice,
      });
    });

    ["ticket", "goods", "plan"].forEach((t) => {
      all[t].sort(
        (a, b) =>
          parseInt(a?.metadata?.display_order || 999, 10) -
          parseInt(b?.metadata?.display_order || 999, 10)
      );
    });

    return all;
  };

  // ★Shop を開く：待たずに即 open（A）
  const handleOpenShop = () => {
    setShowShop(true);
  };

  useEffect(() => {
    const shouldResume = consumeResumeShopAfterAuth();
    if (!shouldResume) return;

    const intent = loadCheckoutIntent();

    if (intent?.type === "OPEN_BOOKING") {
      setBookingResumeDraft(intent?.bookingDraft || null);
      setBookingMounted(true);

      requestAnimationFrame(() => {
        setShowBooking(true);
      });

      clearCheckoutIntent();
      return;
    }

    setShowShop(true);
  }, []);

  const handleOpenBooking = useCallback(() => {
    // すでにマウント済みなら何もしない（連打事故防止）
    setBookingMounted((prev) => {
      if (prev) return prev;
      return true;
    });

    requestAnimationFrame(() => {
      setShowBooking(true);
    });
  }, []);

  const handleCloseBooking = useCallback(() => {
    setShowBooking(false);
    setTimeout(() => {
      setBookingMounted(false);
      setBookingResumeDraft(null);
    }, 220);
  }, []);

  // ✅ products を購読（C）
  // - includeMetadataChanges を有効にして「まず cache → 次に server」で自動反映
  useEffect(() => {
    if (!user) return;

    const q = query(
      tCollection("products", TENANT_ID),
      where("active", "==", true)
    );

    setShopCatalogReady(false);

    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        const fromCache = snap.metadata.fromCache;
        const all = buildCatalogFromProductsSnap(snap);

        setShopCatalog(all);
        setShopCatalogReady(true);

        console.log("[ShopCatalog] snapshot:", {
          size: snap.size,
          fromCache,
          path: q?._query?.path || q?.path || "(unknown)",
        });
      },
      (e) => {
        console.error("[ShopCatalog] onSnapshot error:", {
          code: e?.code,
          message: e?.message,
        }, e);

        // エラー時も UI を落とさない
        setShopCatalog({ ticket: [], goods: [], plan: [] });
        setShopCatalogReady(true);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);


  // ✅ Booking data prefetch（level1）
  useEffect(() => {
    if (!user) return;

    const cKey = getBookingCustomerKey();
    if (!cKey) return;

    let cancelled = false;
    setBookingPrefetchReady(false);

    (async () => {
      try {
        // 1) services
        const sSnap = await getDocs(
          query(
            tCollection('services', TENANT_ID),
            where('is_active', '==', true)
          )
        );
        const services = sSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // 2) products -> productMap
        const pSnap = await getDocs(tCollection('products', TENANT_ID));
        const productMap = {};
        pSnap.forEach((docSnap) => {
          productMap[docSnap.id] = docSnap.data();
        });

        // 3) myTickets（entitlements -> ticket）
        let myTickets = [];
        try {
          const eSnap = await getDocs(tSubCollection('customers', cKey, 'entitlements', TENANT_ID));
          myTickets = buildTicketsFromEntitlementsSnap(eSnap);
        } catch (e) {
          console.warn('[BookingPrefetch] entitlements getDocs failed:', e?.message);
        }

        // 4) subscriptions（会員判定用）
        let activeSubscriptions = [];
        try {
          const subSnap = await getDocs(
            query(
              tSubCollection('customers', cKey, 'subscriptions', TENANT_ID),
              where('status', 'in', ['active', 'trialing'])
            )
          );
          activeSubscriptions = subSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn('[BookingPrefetch] subscriptions getDocs failed:', e?.message);
        }

        if (cancelled) return;

        setBookingPrefetch({
          services,
          productMap,
          myTickets,
          activeSubscriptions,
        });
        setBookingPrefetchReady(true);
      } catch (e) {
        console.error('[BookingPrefetch] failed:', e);
        if (cancelled) return;
        setBookingPrefetch({
          services: [],
          productMap: {},
          myTickets: [],
          activeSubscriptions: [],
        });
        setBookingPrefetchReady(true); // UIは止めない
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, getBookingCustomerKey]);

    const headerColor = String(appConfig?.header_color || '#0B1220');
    const logoUrl = String(appConfig?.logo_url || '').trim();
    const websiteUrl = String(appConfig.website_url || '').trim();
    const showWebsiteLink = isValidHttpUrl(websiteUrl);

    useEffect(() => {
      if (!showWebsiteLink || !websiteUrl) return;

      let origin = "";
      try {
        origin = new URL(websiteUrl).origin;
      } catch {
        return;
      }

      const key = `website-preconnect-${origin}`;

      if (document.querySelector(`link[data-key="${key}"]`)) return;

      const dns = document.createElement("link");
      dns.rel = "dns-prefetch";
      dns.href = origin;
      dns.setAttribute("data-key", key);

      const preconnect = document.createElement("link");
      preconnect.rel = "preconnect";
      preconnect.href = origin;
      preconnect.crossOrigin = "anonymous";
      preconnect.setAttribute("data-key", key);

      document.head.appendChild(dns);
      document.head.appendChild(preconnect);

      return () => {
        dns.remove();
        preconnect.remove();
      };
    }, [showWebsiteLink, websiteUrl]);



  if (loading || checkingGender) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader size={32} />
      </div>
    );
  }

  const profile = memberData || { name: 'ゲストユーザー', metadata: {} };
  const displayName =
    profile?.name && String(profile.name).trim().length > 0
      ? profile.name
      : "ゲスト";
  // gender check (初回必須)


  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-32 text-gray-900 relative overflow-x-hidden ">

      <div
        className="fixed top-0 left-0 right-0 pointer-events-none"
        style={{
        // iPhone PWA で status bar 背景の白透けを抑えるため、
        // safe-area より少しだけ下まで背景を伸ばす
          height: 'calc(env(safe-area-inset-top) + 6px)',
          background: 'rgba(37, 37, 37, 1)',
          zIndex: 30,
        }}
      />

<header
  className="pt-8 pb-16 px-6 rounded-b-[2.25rem] relative overflow-hidden shadow-xl"
  style={{ backgroundColor: headerColor }}
>

        <div className="flex justify-between items-center max-w-[380px] mx-auto relative z-10">
          <div className="flex items-center gap-3">
            {/* ✅ 左上アイコンは Firestore system/app_config の logo_url を使う */}
            <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-md border border-white/20 overflow-hidden">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={appConfig?.app_title || 'logo'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <Sparkles className="text-white" size={18} strokeWidth={2.5} />
              )}
            </div>

            <div className="flex flex-col">
              <h1 className="font-black text-lm leading-tight text-white tracking-tight">
                {displayName}
                <span className="text-[10px] font-medium opacity-60 uppercase tracking-tighter"> さん</span>
              </h1>
              {hasActivePlan && (
                <div className="flex items-center gap-1 mt-0.5">
                  <div className="w-[3px] h-[3px] rounded-full bg-amber-300 animate-pulse"></div>
                  <p className="text-[9px] font-black text-amber-300 uppercase tracking-widest">
                    MEMBER
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* 右：公式サイト（website_url が有効な時のみ表示） */}
          <div className="flex items-center gap-2">
            {/* ホーム画面に追加: 入口は上のバーのボタン(ホームの中には帯を出さない)・中身のシートは共通部品【10-11】 */}
            {install.canShow && (
              <button
                type="button"
                onClick={() => setShowInstallGuide(true)}
                className="bg-white/5 px-3 py-2 rounded-xl text-white/80 hover:bg-white/10 transition-colors text-[11px] font-bold"
                aria-label="ホーム画面に追加"
                title="ホーム画面に追加"
              >
                ホーム画面に追加
              </button>
            )}

            {showWebsiteLink && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noreferrer"
                className="bg-white/5 p-2 rounded-xl text-white/80 hover:bg-white/10 transition-colors"
                aria-label="公式サイトを開く"
                title="公式サイトを開く"
              >
                <Globe size={20} />
              </a>
            )}
          </div>

        </div>
      </header>

        <div className="px-5 -mt-12 space-y-2.5 max-w-[380px] mx-auto relative z-20">
          
          <MemberQRCode user={user} profile={profile} />
          <FacilityStatusCard />

          <ActiveTimeBlock
            activeItems={activeItems}
            clearActiveItem={clearActiveItem}
            className="mt-2"
          />

          <ActiveTimeBlock
            activeItems={activeReservationItem ? [activeReservationItem] : []}
            clearActiveItem={clearActiveReservationItem}
            className="mt-2"
          />

          <DashboardMessageBlock
            customerUid={customerUid}
            profile={profile}
            rawPaymentHistory={rawPaymentHistory}
          />
        

        <div ref={possessionsRef} className="scroll-mt-[120px]">
          <PossessionsCard
            user={user}
            inventory={inventory}
            activeSubscriptions={activeSubscriptions}
            productMap={productMap}
            entitlements={entitlements}
            pendingGifts={pendingGifts}
            highlightGiftToken={highlightGiftToken}
            isOpen={activeAccordion === 'possessions'}
            onToggle={() => handleToggle('possessions')}
            onOpenShop={handleOpenShop}
          />
        </div>

        <div ref={reservationsRef} className="scroll-mt-[120px]">
          <ReservationsCard
            reservations={reservations}
            pendingReservationCount={pendingReservationCount}
            isOpen={activeAccordion === 'reservations'}
            onToggle={() => handleToggle('reservations')}
            onOpenBooking={handleOpenBooking}
            highlightReservationId={highlightReservationIdOnce}
            highlightInviteToken={highlightInviteTokenOnce}
          />
        </div>

        <div ref={statusRef} className="scroll-mt-[120px]">
          <AccountStatusCard
            plan={planActiveEnt}
            profile={profile}
            isOpen={activeAccordion === 'status'}
            onToggle={() => handleToggle('status')}
            onEditProfile={() => setShowProfileEdit(true)}
            onOpenPaymentHistory={() => setShowHistory(true)}
            onLogout={() => signOut(auth)}
          />

          {/* LINE連携 / 電話番号認証の案内（両方そろっている人には出ない） */}
          <AccountLinkCard profile={profile} />
        </div>
      </div>

      <Suspense fallback={null}>
      {/* Shop（プリロード済みデータを渡す） */}
      {showShop && (
        <ShopModalLocal
          isOpen={showShop}
          userProfile={memberData}
          profileSourceReady={profileReady}
          onClose={() => setShowShop(false)}
          preloadedProductsByType={shopCatalog}
          preloadedReady={shopCatalogReady}
        />
      )}

      {bookingMounted && (
        <BookingModal
          isOpen={showBooking}
          userId={user.uid}
          userName={profile.name}
          customerUid={customerUid || null}
          onClose={handleCloseBooking}
          onBookingComplete={() => {}}
          preloaded={bookingPrefetch}
          preloadedReady={bookingPrefetchReady}
          initialBookingDraft={bookingResumeDraft}
        />
      )}

      {showHistory && (
      <PaymentHistoryModal
        isOpen={showHistory}
        history={rawPaymentHistory}
        onClose={() => setShowHistory(false)}
        entitlements={entitlements} 
        planCard={planCard}
        planActiveEnt={planActiveEnt}   // ← 追加
      />
      )}

      {/* 初回 gender 必須モーダル */}
      {showGenderModal && (
        <ProfileEditForm
          initialData={profile}
          firebaseUser={auth.currentUser}
          onSave={() => {
            setShowGenderModal(false);
            setProfilePromptReason("");
          }}
          onCancel={() => {}}
          isFirstVisit={false}
          isShopRedirect={false}
          forceCompleteProfile={true}
          promptReason={profilePromptReason}
        />
      )}

      {/* 通常プロフィール編集 */}
      {showProfileEdit && (
        <ProfileEditForm
          initialData={profile}
          firebaseUser={auth.currentUser}
          onSave={() => {
            setShowProfileEdit(false);
            setProfilePromptReason("");
          }}
          onCancel={() => setShowProfileEdit(false)}
          isFirstVisit={false}
          isShopRedirect={false}
          promptReason=""
        />
      )}
      </Suspense>

      <InstallGuideSheet
        open={showInstallGuide}
        onClose={() => setShowInstallGuide(false)}
        platform={install.platform}
        promptEvent={install.promptEvent}
      />

      <style>{`
        @keyframes shimmer { 100% { transform: translateX(100%); } }
        @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-up { animation: slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fade-in { animation: fadeIn 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bounce-subtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(3px); } }
        .animate-bounce-subtle { animation: bounce-subtle 2s infinite ease-in-out; }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
      `}</style>
      <PoweredByAkuto />
    </div>
  );
};

const Dashboard = ({ user }) => {
  const [startFreshProfileRequest, setStartFreshProfileRequest] = useState(null);

  const handleStartFreshProfile = useCallback(({ reason, deviceId }) => {
    setStartFreshProfileRequest({
      reason: reason || "start_fresh_profile",
      deviceId: deviceId || "",
      requestedAt: Date.now(),
    });
  }, []);

  const handleStartFreshProfileConsumed = useCallback(() => {
    setStartFreshProfileRequest(null);
  }, []);

  return (
    <DeviceAccountGate
      user={user}
      onStartFreshProfile={handleStartFreshProfile}
    >
      <DashboardInner
        user={user}
        startFreshProfileRequest={startFreshProfileRequest}
        onStartFreshProfileConsumed={handleStartFreshProfileConsumed}
      />
    </DeviceAccountGate>
  );
};

export default Dashboard;
//完全動作版テナント済み
